import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, Clock, X } from 'lucide-react';
import { getToken } from '@shared/services/api';
import { fetchShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { getSubscriptionTone, isSubscriptionDismissible, type Tone } from '@shared/config/theme';
import {
  toneChipClasses,
  toneTextClasses,
  toneIconBgClasses,
  toneDotClasses,
} from '@shared/utils/statusTone';

interface SubscriptionStatus {
  status: 'ACTIVE' | 'TRIALING' | 'CANCELED' | 'PAST_DUE' | 'INCOMPLETE' | 'INACTIVE' | 'NONE';
  effectiveStatus?:
    | 'ACTIVE'
    | 'TRIALING'
    | 'CANCELED'
    | 'PAST_DUE'
    | 'INCOMPLETE'
    | 'INACTIVE'
    | 'NONE';
  planName?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  cancelAtPeriodEnd?: boolean;
  daysRemaining?: number;
}

export default function SubscriptionBanner() {
  const { t } = useTranslation('billing');
  const location = useLocation();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = getToken();
        if (!token) return;

        // Shared with TrialBadge, which hits the same endpoint — one request per
        // page load instead of one per component.
        const data = await fetchShared<{ subscription?: SubscriptionStatus }>(
          SHARED_KEYS.subscription,
          async () => {
            const response = await fetch('/api/v1/billing/subscription', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch subscription');
            return response.json();
          }
        );

        if (data.subscription) {
          const daysRemaining = calculateDaysRemaining(data.subscription);
          // Use effectiveStatus if provided by backend, otherwise detect expired ACTIVE locally
          let effectiveStatus = data.subscription.effectiveStatus;
          if (
            !effectiveStatus &&
            data.subscription.status === 'ACTIVE' &&
            daysRemaining !== undefined &&
            daysRemaining <= 0
          ) {
            effectiveStatus = 'INACTIVE';
          }
          setSubscription({
            status: data.subscription.status,
            effectiveStatus: effectiveStatus,
            planName: data.subscription.planName,
            currentPeriodEnd: data.subscription.currentPeriodEnd,
            trialEnd: data.subscription.trialEnd,
            cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd,
            daysRemaining: daysRemaining,
          });
        } else {
          setSubscription({ status: 'NONE' });
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        setSubscription({ status: 'NONE' });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
    window.addEventListener('pgkb:subscription-changed', fetchSubscription);
    return () => window.removeEventListener('pgkb:subscription-changed', fetchSubscription);
  }, []);

  // Animate in after loading
  useEffect(() => {
    if (!loading && shouldShowBanner(subscription)) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading, subscription]);

  // Reset dismiss state when route changes (but not for same page navigation)
  useEffect(() => {
    setIsDismissed(false);
  }, [location.pathname]);

  if (loading || !subscription || isDismissed || !shouldShowBanner(subscription)) {
    return null;
  }

  const bannerConfig = getBannerConfig(subscription, t);

  return (
    <div
      id="subscription-banner"
      className={`transition-all duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden py-0 border-0'
      }`}
    >
      <div className={`${bannerConfig.bgColor} ${bannerConfig.borderColor} border-b-4 shadow-lg`}>
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Icon and Message */}
            <div className="flex items-start sm:items-center space-x-3 flex-1 min-w-0">
              {/* Icon */}
              <div className={`flex-shrink-0 p-2 rounded-lg ${bannerConfig.iconBgColor}`}>
                {bannerConfig.icon}
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${bannerConfig.textColor}`}>
                  {bannerConfig.title}
                </p>
                <p className={`text-xs sm:text-sm mt-0.5 ${bannerConfig.messageColor}`}>
                  {bannerConfig.message}
                  {subscription.daysRemaining !== undefined && subscription.daysRemaining > 0 && (
                    <span className="font-semibold">
                      {' '}
                      ({subscription.daysRemaining} days remaining)
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* CTA Button */}
              <Link
                to={bannerConfig.ctaLink}
                className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-bold transition-all hover:scale-105 active:scale-95 ${bannerConfig.buttonClass}`}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {bannerConfig.ctaText}
              </Link>

              {/* Dismiss button - only for warnings, not critical alerts */}
              {bannerConfig.canDismiss && (
                <button
                  type="button"
                  onClick={() => setIsDismissed(true)}
                  className={`p-2 rounded-lg transition-colors ${bannerConfig.dismissButtonClass}`}
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function calculateDaysRemaining(sub: any): number | undefined {
  if (!sub) return undefined;

  const now = new Date();
  let endDate: Date | null = null;

  if (sub.status === 'TRIALING' && sub.trialEnd) {
    endDate = new Date(sub.trialEnd);
  } else if (sub.currentPeriodEnd) {
    endDate = new Date(sub.currentPeriodEnd);
  }

  if (endDate) {
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  return undefined;
}

function shouldShowBanner(sub: SubscriptionStatus | null): boolean {
  if (!sub) return false;

  // Prefer effectiveStatus (computed by backend) over raw status
  const statusToCheck = sub.effectiveStatus || sub.status;

  // Show banner for these statuses
  const showStatuses = ['NONE', 'CANCELED', 'PAST_DUE', 'INCOMPLETE', 'INACTIVE'];
  if (showStatuses.includes(statusToCheck)) return true;

  // Show for trial ending soon (less than 3 days)
  if (statusToCheck === 'TRIALING' && sub.daysRemaining !== undefined && sub.daysRemaining <= 3) {
    return true;
  }

  // Show for subscription ending soon (cancel_at_period_end and less than 3 days)
  if (
    statusToCheck === 'ACTIVE' &&
    sub.cancelAtPeriodEnd &&
    sub.daysRemaining !== undefined &&
    sub.daysRemaining <= 3
  ) {
    return true;
  }

  return false;
}

/**
 * Solid tone colour for the 4px accent strip — the chip border is only 20% alpha,
 * which is invisible at 4px. Static strings only: Tailwind cannot see
 * runtime-assembled class names.
 */
const TONE_BORDER: Record<Tone, string> = {
  ok: 'border-state-ok-light dark:border-state-ok-dark',
  warn: 'border-state-warn-light dark:border-state-warn-dark',
  danger: 'border-state-danger-light dark:border-state-danger-dark',
  info: 'border-state-info-light dark:border-state-info-dark',
  neutral: 'border-surface-border-light dark:border-surface-border-dark',
  accent: 'border-brand-600 dark:border-brand-400',
};

/**
 * The colour half of a banner entry. Every hue comes from the shared tone
 * contract, so a status can never drift from the rest of the app.
 *
 * `bgColor` uses `toneIconBgClasses` (tone surface + foreground) rather than
 * `toneChipClasses`: the chip string bundles a `/20` border that would fight
 * the solid 4px accent strip for the same CSS property. `iconBgColor` keeps the
 * chip form (surface + border) because the icon container is its own element.
 */
function toneSlots(tone: Tone) {
  return {
    bgColor: toneIconBgClasses[tone],
    borderColor: TONE_BORDER[tone],
    textColor: toneTextClasses[tone],
    messageColor: toneTextClasses[tone],
    iconBgColor: toneChipClasses[tone],
    buttonClass: `${toneDotClasses[tone]} text-white dark:text-on-accent-dark hover:opacity-90`,
    dismissButtonClass: `${toneTextClasses[tone]} hover:bg-surface-2-light dark:hover:bg-surface-2-dark`,
  };
}

function getBannerConfig(sub: SubscriptionStatus, t: (key: string) => string) {
  const configs: Record<string, any> = {
    NONE: {
      ...toneSlots(getSubscriptionTone('NONE')),
      title: t('banner.none.title'),
      message: t('banner.none.message'),
      ctaText: t('banner.none.cta'),
      ctaLink: '/billing',
      icon: <AlertTriangle className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('NONE'),
    },
    CANCELED: {
      ...toneSlots(getSubscriptionTone('CANCELED')),
      title: t('banner.canceled.title'),
      message: t('banner.canceled.message'),
      ctaText: t('banner.canceled.cta'),
      ctaLink: '/billing',
      icon: <AlertTriangle className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('CANCELED'),
    },
    PAST_DUE: {
      ...toneSlots(getSubscriptionTone('PAST_DUE')),
      title: t('banner.pastDue.title'),
      message: t('banner.pastDue.message'),
      ctaText: t('banner.pastDue.cta'),
      ctaLink: '/billing',
      icon: <AlertTriangle className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('PAST_DUE'),
    },
    INCOMPLETE: {
      ...toneSlots(getSubscriptionTone('INCOMPLETE')),
      title: t('banner.incomplete.title'),
      message: t('banner.incomplete.message'),
      ctaText: t('banner.incomplete.cta'),
      ctaLink: '/billing',
      icon: <AlertTriangle className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('INCOMPLETE'),
    },
    INACTIVE: {
      ...toneSlots(getSubscriptionTone('INACTIVE')),
      title: t('banner.inactive.title'),
      message: t('banner.inactive.message'),
      ctaText: t('banner.inactive.cta'),
      ctaLink: '/billing',
      icon: <AlertTriangle className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('INACTIVE'),
    },
    EXPIRED: {
      ...toneSlots(getSubscriptionTone('EXPIRED')),
      title: t('banner.expired.title'),
      message: t('banner.expired.message'),
      ctaText: t('banner.expired.cta'),
      ctaLink: '/billing',
      icon: <AlertTriangle className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('EXPIRED'),
    },
    TRIALING: {
      ...toneSlots(getSubscriptionTone('TRIALING')),
      title: t('banner.trialing.title'),
      message: t('banner.trialing.message'),
      ctaText: t('banner.trialing.cta'),
      ctaLink: '/billing',
      icon: <Clock className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('TRIALING'),
    },
    ACTIVE_CANCELING: {
      ...toneSlots(getSubscriptionTone('ACTIVE_CANCELING')),
      title: t('banner.canceling.title'),
      message: t('banner.canceling.message'),
      ctaText: t('banner.canceling.cta'),
      ctaLink: '/billing',
      icon: <Clock className="w-5 h-5" />,
      canDismiss: isSubscriptionDismissible('ACTIVE_CANCELING'),
    },
  };

  const statusToCheck = sub.effectiveStatus || sub.status;

  // Special case for active subscription that's canceling
  if (statusToCheck === 'ACTIVE' && sub.cancelAtPeriodEnd) {
    return configs.ACTIVE_CANCELING;
  }

  // Use EXPIRED config when an ACTIVE subscription has become INACTIVE due to period expiry
  if (sub.status === 'ACTIVE' && sub.effectiveStatus === 'INACTIVE') {
    return configs.EXPIRED;
  }

  return configs[statusToCheck] || configs.NONE;
}
